// ═══════════════════════════════════════════════════════════════════════
//  AliteCartDrawer — Client-Side JSON Rendering + pureFetch
// ═══════════════════════════════════════════════════════════════════════

// ── NATIVE FETCH ──
// Captured early to bypass any third-party app monkey-patches.
const pureFetch = window.fetch.bind(window);

class AliteCartDrawer {
  constructor() {
    this.drawer = document.getElementById('AliteCartDrawer');
    if (!this.drawer) return;

    const settingsEl = document.getElementById('AliteCartDrawer-Settings');
    this.settings = settingsEl ? JSON.parse(settingsEl.textContent) : {};

    this._timers = {};
    this._footerVersion = 0;
    this._cachedShipping = null;

    const gokwikWrapper = this.drawer.querySelector('.gokwik-checkout');
    this.gokwikHtml = gokwikWrapper ? gokwikWrapper.outerHTML : '';

    this._bindEvents();
  }

  _bindEvents() {
    this._summaryOpen = false;

    this.drawer.addEventListener('click', (e) => {
      if (e.target.closest('[data-cart-close]')) return this.close();

      // Toggle Order Summary
      const toggleRow = e.target.closest('#CartDrawer-EstimatedToggle');
      if (toggleRow) {
        this._toggleSummary();
        return;
      }

      const minusBtn = e.target.closest('.alite-cart-drawer__qty-btn.minus');
      if (minusBtn) {
        const key = minusBtn.dataset.key;
        const input = minusBtn.nextElementSibling;
        const newQty = Math.max(0, parseInt(input.value, 10) - 1);
        this._optimisticUpdate(key, newQty, input);
        return;
      }

      const plusBtn = e.target.closest('.alite-cart-drawer__qty-btn.plus');
      if (plusBtn) {
        const key = plusBtn.dataset.key;
        const input = plusBtn.previousElementSibling;
        const newQty = parseInt(input.value, 10) + 1;
        this._optimisticUpdate(key, newQty, input);
        return;
      }

      const removeBtn = e.target.closest('.alite-cart-drawer__item-remove');
      if (removeBtn) {
        this._optimisticUpdate(removeBtn.dataset.key, 0, null);
        return;
      }

      const upsellBtn = e.target.closest('.alite-cart-drawer__upsell-add');
      if (upsellBtn) {
        this._addItem(upsellBtn.dataset.id);
        return;
      }

      const progressNode = e.target.closest('.alite-cart-drawer__progress-node');
      if (progressNode && progressNode.dataset.variantId) {
        this._addItem(progressNode.dataset.variantId);
        return;
      }
    });

    document.addEventListener('cart:open', (e) => {
      const skipRefresh = e.detail && e.detail.skipRefresh;
      this.open(skipRefresh);
    });
    document.addEventListener('cart:refresh', () => this.refresh());
  }

  _toggleSummary() {
    this._summaryOpen = !this._summaryOpen;
    const toggleRow = document.getElementById('CartDrawer-EstimatedToggle');
    const summary = document.getElementById('CartDrawer-OrderSummary');
    if (toggleRow) toggleRow.classList.toggle('is-open', this._summaryOpen);
    if (summary) summary.classList.toggle('is-open', this._summaryOpen);
  }

  open(skipRefresh) {
    this.drawer.setAttribute('aria-hidden', 'false');
    setTimeout(() => {
      this.drawer.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    }, 10);
    if (!skipRefresh) this.refresh();
  }

  close() {
    this.drawer.classList.remove('is-open');
    document.body.style.overflow = '';
    setTimeout(() => this.drawer.setAttribute('aria-hidden', 'true'), 400);
  }

  _optimisticUpdate(key, newQty, inputEl) {
    const row = this.drawer.querySelector(`.alite-cart-drawer__item[data-key="${key}"]`);

    if (newQty === 0 && row) {
      row.style.transition = 'opacity 0.2s, max-height 0.3s, margin 0.3s';
      row.style.opacity = '0';
      requestAnimationFrame(() => {
        row.style.maxHeight = '0';
        row.style.marginBottom = '0';
        row.style.paddingTop = '0';
        row.style.paddingBottom = '0';
        row.style.overflow = 'hidden';
      });
    } else {
      if (inputEl) inputEl.value = newQty;
    }

    // Show full-area spinner on the items container
    const itemsEl = document.getElementById('CartDrawer-Items');
    if (itemsEl) itemsEl.classList.add('is-loading');

    clearTimeout(this._timers[key]);
    this._timers[key] = setTimeout(() => {
      delete this._timers[key];
      this._updateCart(key, newQty, row);
    }, 300);
  }

  _updateCart(key, quantity, row) {
    pureFetch(window.Shopify.routes.root + 'cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ id: key, quantity: quantity }),
    })
      .then((r) => r.json())
      .then((cart) => {
        this._renderCart(cart);
      })
      .catch((err) => {
        console.error('Cart update error:', err);
        const itemsEl = document.getElementById('CartDrawer-Items');
        if (itemsEl) itemsEl.classList.remove('is-loading');
      });
  }

  _addItem(variantId) {
    const itemsEl = document.getElementById('CartDrawer-Items');
    if (itemsEl) itemsEl.classList.add('is-loading');

    pureFetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] }),
    })
      .then((r) => r.json())
      .then(() => this.refresh())
      .catch((err) => {
        console.error('Cart add error:', err);
        if (itemsEl) itemsEl.classList.remove('is-loading');
      });
  }

  refresh() {
    return pureFetch(window.Shopify.routes.root + 'cart.js')
      .then((r) => r.json())
      .then((cart) => this._renderCart(cart));
  }

  // Section Rendering API: parse server-rendered HTML and swap in dynamic areas
  renderSectionHTML(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');

    // Update empty state visibility
    const newEmpty = doc.querySelector('.alite-cart-drawer__empty-state');
    const curEmpty = this.drawer.querySelector('.alite-cart-drawer__empty-state');
    if (newEmpty && curEmpty) curEmpty.style.display = newEmpty.style.display;

    // Update cart items
    const newItems = doc.getElementById('CartDrawer-Items');
    const curItems = document.getElementById('CartDrawer-Items');
    if (newItems && curItems) {
      curItems.innerHTML = newItems.innerHTML;
      curItems.style.display = newItems.style.display;
      curItems.classList.remove('is-loading');
    }

    // Update progress bar
    const newProgress = doc.getElementById('CartDrawer-Progress');
    const curProgress = document.getElementById('CartDrawer-Progress');
    if (newProgress && curProgress) {
      curProgress.innerHTML = newProgress.innerHTML;
      curProgress.style.display = newProgress.style.display;
    }

    // Footer is managed by client-side JS (_renderFooter) — do NOT swap it here
    // to avoid overwriting JS-computed shipping/totals with server-rendered values.
    const newFooter = doc.getElementById('CartDrawer-Footer');
    const curFooter = document.getElementById('CartDrawer-Footer');
    if (newFooter && curFooter) {
      curFooter.style.display = newFooter.style.display;
    }

    // Update upsells visibility
    const newUpsells = doc.getElementById('CartDrawer-Upsells');
    const curUpsells = document.getElementById('CartDrawer-Upsells');
    if (newUpsells && curUpsells) curUpsells.style.display = newUpsells.style.display;

    // Re-cache GoKwik checkout button HTML for future client-side re-renders
    const gokwikWrapper = this.drawer.querySelector('.gokwik-checkout');
    this.gokwikHtml = gokwikWrapper ? gokwikWrapper.outerHTML : '';

    // Re-init GoKwik SDK so checkout button works
    if (window.gokwikSdk && typeof window.gokwikSdk.init === 'function') {
      window.gokwikSdk.init();
    }

    // Re-inject upsells if needed
    const tpl = document.getElementById('CartDrawer-UpsellTemplate');
    const upsellTarget = document.getElementById('CartDrawer-Upsells');
    if (tpl && upsellTarget && !upsellTarget.hasChildNodes()) {
      upsellTarget.innerHTML = tpl.innerHTML;
    }
  }

  _renderCart(cart) {
    this._renderItems(cart.items);
    this._renderFooter(cart);
    this.updateCartProgress(cart);
    this._updateHeaderCartCount(cart.item_count);
    
    // Dispatch event to sync other components like sticky cart
    document.dispatchEvent(new CustomEvent('alite:cart:updated', { detail: { cart: cart } }));

    // Clear the loading spinner overlay
    const itemsEl = document.getElementById('CartDrawer-Items');
    if (itemsEl) itemsEl.classList.remove('is-loading');

    // Handle empty state visibility
    const emptyStateEl = this.drawer.querySelector('.alite-cart-drawer__empty-state');
    const progressEl = document.getElementById('CartDrawer-Progress');
    const upsellsEl = document.getElementById('CartDrawer-Upsells');
    const footerEl = document.getElementById('CartDrawer-Footer');

    if (cart.item_count === 0) {
      if (emptyStateEl) emptyStateEl.style.display = 'flex';
      if (progressEl) progressEl.style.display = 'none';
      if (itemsEl) itemsEl.style.display = 'none';
      if (upsellsEl) upsellsEl.style.display = 'none';
      if (footerEl) footerEl.style.display = 'none';
    } else {
      if (emptyStateEl) emptyStateEl.style.display = 'none';
      // progressEl display is fully managed by updateCartProgress()
      if (itemsEl) itemsEl.style.display = '';
      if (upsellsEl) upsellsEl.style.display = '';
      if (footerEl) footerEl.style.display = '';
    }
  }

  _updateHeaderCartCount(count) {
    document.querySelectorAll('.alite-header__cart-bubble').forEach(el => {
      el.textContent = count;
      el.style.display = 'flex';
    });
  }

  _renderItems(items) {
    const container = document.getElementById('CartDrawer-Items');
    if (!container) return;

    if (!items || items.length === 0) {
      container.innerHTML = `
        <div style="text-align:center; padding: 40px 0; color: #666;">
          <p>Your cart is empty.</p>
        </div>`;
      return;
    }

    const html = items.map(item => `
      <div class="alite-cart-drawer__item" data-key="${item.key}">
        <img src="${item.image ? item.image.replace(/(\.[^.]+)$/, '_140x$1') : ''}" 
             alt="${item.product_title}" 
             class="alite-cart-drawer__item-img">
        <div class="alite-cart-drawer__item-info">
          <h4 class="alite-cart-drawer__item-title">${item.product_title}</h4>
          <div class="alite-cart-drawer__item-price">${this._formatMoney(item.line_price)}</div>
        </div>
        <div class="alite-cart-drawer__item-actions">
          <button class="alite-cart-drawer__item-remove" data-key="${item.key}" aria-label="Remove">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M10 11v6M14 11v6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <div class="alite-cart-drawer__qty">
            <button class="alite-cart-drawer__qty-btn minus" data-key="${item.key}">-</button>
            <input type="number" class="alite-cart-drawer__qty-input" value="${item.quantity}" data-key="${item.key}" readonly>
            <button class="alite-cart-drawer__qty-btn plus" data-key="${item.key}">+</button>
          </div>
        </div>
      </div>
    `).join('');

    container.innerHTML = html;
  }

  _renderFooter(cart) {
    const container = document.getElementById('CartDrawer-Footer');
    if (!container) return;

    // Increment version to cancel any in-flight renders
    this._footerVersion++;
    const myVersion = this._footerVersion;

    if (!cart || cart.item_count === 0) {
      container.innerHTML = `
        <div class="alite-cart-drawer__checkout-wrapper">
          <form action="/cart" method="post" id="cart-drawer-form">
            <button type="submit" name="checkout" class="alite-cart-drawer__checkout-btn" disabled>Checkout</button>
          </form>
        </div>`;
      return;
    }

    // Gather unique product handles to fetch compare_at_price
    const handles = [...new Set(cart.items.map(item => item.handle))];

    // Fetch product data AND shipping rates in parallel
    Promise.all([
      // 1. Product data for compare_at_price
      Promise.all(
        handles.map(h =>
          pureFetch(`${window.Shopify.routes.root}products/${h}.js`)
            .then(r => r.json())
            .catch(() => null)
        )
      ),
      // 2. Shipping rates (cached after first fetch)
      this._getShippingCost(cart)
    ]).then(([products, shippingCost]) => {
      // Abort if a newer render has started
      if (myVersion !== this._footerVersion) return;

      // Build variantId → compare_at_price map
      const compareMap = {};
      products.forEach(product => {
        if (!product || !product.variants) return;
        product.variants.forEach(v => {
          compareMap[v.id] = v.compare_at_price;
        });
      });

      // MRP total: use compare_at_price where available
      let mrpTotal = 0;
      cart.items.forEach(item => {
        const comparePrice = compareMap[item.variant_id];
        if (comparePrice && comparePrice > item.original_price) {
          mrpTotal += comparePrice * item.quantity;
        } else {
          mrpTotal += item.original_line_price;
        }
      });

      this._buildAndRenderFooter(container, cart, mrpTotal, shippingCost);
    }).catch(err => {
      if (myVersion !== this._footerVersion) return;
      console.error('Error fetching data for cart summary:', err);
      this._buildAndRenderFooter(container, cart, cart.original_total_price, 4900);
    });
  }

  // Returns shipping cost in paise. Fetches from Shopify API, caches result.
  // Re-fetches when cart total changes (shipping may change at ₹300 threshold).
  _getShippingCost(cart) {
    // Use items-based total for cache key (immune to SDK modifications on cart.total_price)
    const cartTotal = cart ? cart.items.reduce((sum, item) => sum + item.final_line_price, 0) : 0;
    // Use cache if available and cart total hasn't changed
    if (this._cachedShipping !== null && this._cachedShippingCartTotal === cartTotal) {
      return Promise.resolve(this._cachedShipping);
    }

    return pureFetch(`${window.Shopify.routes.root}cart/shipping_rates.json?shipping_address[zip]=400001&shipping_address[country]=IN&shipping_address[province]=Maharashtra`)
      .then(r => {
        if (!r.ok) throw new Error('Shipping rates unavailable');
        return r.json();
      })
      .then(data => {
        const rates = data.shipping_rates || [];
        if (rates.length === 0) {
          this._cachedShipping = 4900;
          this._cachedShippingCartTotal = cartTotal;
          return 4900;
        }

        const cheapest = rates.reduce((min, rate) =>
          parseFloat(rate.price) < parseFloat(min.price) ? rate : min
        , rates[0]);

        const cost = Math.round(parseFloat(cheapest.price) * 100);
        this._cachedShipping = cost;
        this._cachedShippingCartTotal = cartTotal;
        return cost;
      })
      .catch(() => {
        this._cachedShipping = 4900;
        this._cachedShippingCartTotal = cartTotal;
        return 4900;
      });
  }

  _buildAndRenderFooter(container, cart, mrpTotal, shippingCost) {
    // Compute actual cart total from items directly (immune to SDK modifications on cart.total_price)
    const cartFinalTotal = cart.items.reduce((sum, item) => sum + item.final_line_price, 0);
    const cartSubtotal = cart.items.reduce((sum, item) => sum + item.original_line_price, 0);
    const discountOnMRP = Math.max(0, mrpTotal - cartSubtotal);
    const totalDiscount = Math.max(0, cartSubtotal - cartFinalTotal);
    const totalSavings = discountOnMRP + totalDiscount;
    const estimatedTotal = cartFinalTotal + shippingCost;
    const offPercent = mrpTotal > 0 ? Math.round((totalSavings / mrpTotal) * 100) : 0;

    const isShippingFree = (shippingCost === 0);
    const shippingDisplay = isShippingFree
      ? '<span style="color:#10b981;font-weight:600;">FREE</span>'
      : this._formatMoney(shippingCost);

    const openClass = this._summaryOpen ? ' is-open' : '';

    container.innerHTML = `
      <div class="alite-cart-drawer__savings-banner" id="CartDrawer-SavingsBanner">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <circle cx="7" cy="7" r="1" fill="#fff"/>
        </svg>
        <span id="CartDrawer-SavingsBannerText">${this._formatMoney(totalSavings)} Saved so far!</span>
      </div>

      <div class="alite-cart-drawer__estimated-row${openClass}" id="CartDrawer-EstimatedToggle">
        <div class="alite-cart-drawer__estimated-left">
          <svg class="alite-cart-drawer__estimated-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" stroke="#1a3c6e" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M3.27 6.96L12 12.01l8.73-5.05M12 22.08V12" stroke="#1a3c6e" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span class="alite-cart-drawer__estimated-label">Estimated Total</span>
          <svg class="alite-cart-drawer__estimated-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <div class="alite-cart-drawer__estimated-right">
          <s class="alite-cart-drawer__estimated-compare">${this._formatMoney(mrpTotal)}</s>
          <span class="alite-cart-drawer__estimated-price">${this._formatMoney(estimatedTotal)}</span>
          ${offPercent > 0 ? `<span class="alite-cart-drawer__estimated-off">(${offPercent}% OFF)</span>` : ''}
        </div>
      </div>

      <div class="alite-cart-drawer__order-summary${openClass}" id="CartDrawer-OrderSummary">
        <div class="alite-cart-drawer__summary-header">
          <h3>Order Summary</h3>
          <span class="alite-cart-drawer__summary-saved">${this._formatMoney(totalSavings)} saved so far</span>
        </div>
        <div class="alite-cart-drawer__summary-rows">
          <div class="alite-cart-drawer__summary-row">
            <span>MRP total</span>
            <span>${this._formatMoney(mrpTotal)}</span>
          </div>
          <div class="alite-cart-drawer__summary-row alite-cart-drawer__summary-row--green">
            <span>Discount on MRP</span>
            <span>${this._formatMoney(discountOnMRP)}</span>
          </div>
          <div class="alite-cart-drawer__summary-row">
            <span>Cart Subtotal</span>
            <span>${this._formatMoney(cartSubtotal)}</span>
          </div>
          <div class="alite-cart-drawer__summary-row alite-cart-drawer__summary-row--green">
            <span>Total discount</span>
            <span>${this._formatMoney(totalDiscount)}</span>
          </div>
          <div class="alite-cart-drawer__summary-row">
            <span>Shipping Charges</span>
            <span id="CartDrawer-SummaryShipping">${shippingDisplay}</span>
          </div>
          <div class="alite-cart-drawer__summary-row alite-cart-drawer__summary-row--green alite-cart-drawer__summary-row--bold">
            <span>Total savings</span>
            <span>${this._formatMoney(totalSavings)}</span>
          </div>
          <div class="alite-cart-drawer__summary-row alite-cart-drawer__summary-row--total">
            <span>Estimated Total</span>
            <span>${this._formatMoney(estimatedTotal)}</span>
          </div>
        </div>
      </div>

      <div class="alite-cart-drawer__checkout-wrapper">
        ${this.gokwikHtml || `
          <form action="/cart" method="post" id="cart-drawer-form">
            <button type="submit" name="checkout" class="alite-cart-drawer__checkout-btn">
              <span class="alite-cart-drawer__checkout-btn-text">
                <span>Checkout Now</span>
              </span>
              <span class="alite-cart-drawer__checkout-btn-icons">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 5l7 7-7 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </span>
            </button>
          </form>
        `}
      </div>
    `;

    if (window.gokwikSdk && typeof window.gokwikSdk.init === 'function') {
      window.gokwikSdk.init();
    }
  }

  updateCartProgress(cart) {
    const container = document.getElementById('CartDrawer-Progress');
    if (!container) return;

    // 1. Calculate Target Quantity
    const count = cart.item_count;

    // 2. Update Progress Fill Line
    let progressPercent = (count / 3) * 100;
    if (progressPercent > 100) progressPercent = 100;
    const fillLine = document.querySelector('.alite-cart-drawer__progress-fill');
    if (fillLine) {
      fillLine.style.width = `${progressPercent}%`;
    }

    // 3. Update the 3 Nodes
    const nodes = document.querySelectorAll('.alite-cart-drawer__progress-node');
    nodes.forEach((node, index) => {
      if (count >= (index + 1)) {
        node.classList.add('is-completed');
      } else {
        node.classList.remove('is-completed');
      }
    });

    // 4. Dynamic Header Text
    const headerText = document.querySelector('.alite-cart-drawer__progress-text');
    if (headerText) {
      let newText = '';
      if (count === 1) {
        newText = 'Add <strong>1 more product</strong> to unlock <strong>Pack of 2</strong> discounts!';
      } else if (count === 2) {
        newText = 'Add <strong>1 more product</strong> to unlock <strong>Pack of 3</strong> discounts!';
      } else if (count >= 3) {
        newText = "🎉 You've unlocked the maximum pack discounts!";
      } else {
        newText = 'Add products to unlock rewards!';
      }

      // Preserve the SVG icon if it exists
      const iconHTML = `
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="#ff6b6b"/>
        </svg>
      `;
      headerText.innerHTML = `${iconHTML} ${newText}`;
    }

    // 5. Fetch product variations and attach them to nodes
    if (cart.items.length > 0) {
      const handle = cart.items[0].handle;
      pureFetch(window.Shopify.routes.root + 'products/' + handle + '.js')
        .then(r => {
          if (!r.ok) throw new Error('Product not found');
          return r.json();
        })
        .then(product => {
          // Only show the progress bar if the product has actual variations (more than 1)
          if (product && product.variants && product.variants.length > 1 && product.variants[0].title !== 'Default Title') {
            container.style.display = 'block';
            nodes.forEach((node, index) => {
              if (product.variants[index]) {
                const variant = product.variants[index];
                node.dataset.variantId = variant.id;
                node.style.cursor = 'pointer';

                // Dynamically update the node text
                const textSpan = node.querySelector('span');
                if (textSpan) {
                  const formattedPrice = this._formatMoney(variant.price).replace('.00', '');
                  textSpan.innerHTML = `${variant.title}<br>@ ${formattedPrice}`;
                }
              } else {
                delete node.dataset.variantId;
                node.style.cursor = 'default';
              }
            });
          } else {
            // Hide progress bar if no variations
            container.style.display = 'none';
          }
        })
        .catch(err => {
          console.error('Error fetching variants for progress nodes:', err);
          container.style.display = 'none';
        });
    } else {
      container.style.display = 'none';
      nodes.forEach(node => {
        delete node.dataset.variantId;
        node.style.cursor = 'default';
      });
    }
  }

  _formatMoney(cents) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    let value = '';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = this.settings.money_format || '₹{{amount}}';

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = precision || 2;
      thousands = thousands || ',';
      decimal = decimal || '.';
      if (isNaN(number) || number == null) return '0.00';
      number = (number / 100.0).toFixed(precision);
      const parts = number.split('.');
      const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const centsAmount = parts[1] ? decimal + parts[1] : '';
      return dollarsAmount + centsAmount;
    }

    const match = formatString.match(placeholderRegex);
    if (!match) return cents;

    switch (match[1]) {
      case 'amount': value = formatWithDelimiters(cents, 2); break;
      case 'amount_no_decimals': value = formatWithDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = formatWithDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = formatWithDelimiters(cents, 0, '.', ','); break;
      default: value = formatWithDelimiters(cents, 2); break;
    }

    return formatString.replace(placeholderRegex, value).replace('Rs.', '₹').replace('Rs ', '₹').replace('₹ ', '₹');
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.aliteCartDrawer = new AliteCartDrawer();
});
